import { useState } from 'react'
import { Group, Loader, Modal, Select, Stack, Text, Title } from '@common/ui'
import { com } from '@eidu/web'
import { useQuery } from '@tanstack/react-query'
import useResource from '../../../../../io/useResource'
import { VersionRecord } from '../../../../../io/api/entity/data/listVersions'
import fetchVersionSnapshot from '../../../../../io/api/entity/data/fetchVersionSnapshot'
import MergeEditor from '../../../../../components/data/MergeEditor'
import DiffStats from './DiffStats'
import EntityDiffSummary from './EntityDiffSummary'
import { diffSnapshots } from './entitySnapshotDiff'
import EntityType = com.eidu.entity.type.EntityType

type VersionDiffDialogProps = {
  onClose: () => void
  records: VersionRecord[]
  initialLeftVersionId: string
  initialRightVersionId: string
  entityType?: EntityType
}

const useSnapshot = (snapshotResourceKey: string | undefined) =>
  useQuery({
    queryKey: ['versionSnapshot', snapshotResourceKey],
    queryFn: () => fetchVersionSnapshot(snapshotResourceKey!),
    enabled: !!snapshotResourceKey,
  })

const shortVersionId = (versionId: string): string => versionId.split('-')[0] ?? versionId.slice(0, 8)
const absoluteTimestamp = (ms: number): string => new Date(ms).toLocaleString()

const VersionSelect = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (versionId: string) => void
  options: VersionRecord[]
}) => (
  <Select
    size="sm"
    style={{ minWidth: 220 }}
    label={label}
    value={value}
    onChange={(next) => next && onChange(next)}
    allowDeselect={false}
    data={options.map((r) => ({
      value: r.versionId,
      label: `${shortVersionId(r.versionId)} · ${absoluteTimestamp(r.createdAt)}`,
    }))}
  />
)

const VersionDiffDialog = ({
  onClose,
  records,
  initialLeftVersionId,
  initialRightVersionId,
  entityType,
}: VersionDiffDialogProps) => {
  const [leftVersionId, setLeftVersionId] = useState(initialLeftVersionId)
  const [rightVersionId, setRightVersionId] = useState(initialRightVersionId)

  const left = records.find((r) => r.versionId === leftVersionId)
  const right = records.find((r) => r.versionId === rightVersionId)

  const { content: leftContent, isLoading: leftLoading, error: leftError } = useResource(left?.payloadKey ?? undefined)
  const {
    content: rightContent,
    isLoading: rightLoading,
    error: rightError,
  } = useResource(right?.payloadKey ?? undefined)

  const leftSnapshotQuery = useSnapshot(left?.snapshotResourceKey)
  const rightSnapshotQuery = useSnapshot(right?.snapshotResourceKey)
  const entityDiff =
    leftSnapshotQuery.data && rightSnapshotQuery.data
      ? diffSnapshots(leftSnapshotQuery.data, rightSnapshotQuery.data)
      : undefined

  const loading = leftLoading || rightLoading
  const error = leftError || rightError
  const ready = !loading && !error && leftContent !== undefined && rightContent !== undefined

  return (
    <Modal
      opened
      onClose={onClose}
      size="80rem"
      title={
        <Stack gap={0}>
          <Text size="xs" tt="uppercase" c="dimmed" lts={1}>
            Compare
          </Text>
          <Title order={4}>Version diff</Title>
          <Group gap="md" mt="sm" align="flex-end">
            <VersionSelect label="Base" value={leftVersionId} onChange={setLeftVersionId} options={records} />
            <Text c="dimmed">→</Text>
            <VersionSelect label="Compare" value={rightVersionId} onChange={setRightVersionId} options={records} />
            {ready && <DiffStats left={leftContent} right={rightContent} />}
          </Group>
        </Stack>
      }
      styles={{ body: { height: '70vh', display: 'flex', flexDirection: 'column', padding: 0 } }}
    >
      {entityDiff && (
        <div style={{ padding: 16, borderBottom: '1px solid var(--trellis-color-outline)' }}>
          <Text size="sm" fw={600} mb="xxs">
            Fields & links
          </Text>
          <EntityDiffSummary
            diff={entityDiff}
            entityType={entityType}
            beforeLabel={`Base · ${shortVersionId(leftVersionId)}`}
            afterLabel={`Compare · ${shortVersionId(rightVersionId)}`}
          />
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Text size="sm" fw={600} px="md" pt="sm" pb="xxs">
          Payload
        </Text>
        {loading && (
          <Stack flex={1} align="center" justify="center">
            <Loader />
          </Stack>
        )}
        {error && (
          <Stack flex={1} align="center" justify="center" px="lg">
            <Text c="negative">Failed to load diff: {error.message}</Text>
          </Stack>
        )}
        {ready && <MergeEditor left={leftContent} right={rightContent} />}
      </div>
    </Modal>
  )
}

export default VersionDiffDialog
